import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type {
  AuthStrategy,
  LoginResponse,
  RegisterUser,
} from '@indutrust/auth-model';
import { EnvironmentService } from '@indutrust/shared-env';
import type { User } from '@indutrust/shared-model';

/**
 * Wraps a body in the Rails-style envelope the backend expects, e.g.
 * `wrap('session', { email, password })` → `{ session: { email, password } }`.
 *
 * Kept as a local helper so consumers (stores / components) never see the
 * Rails shape. The audit rationale is documented in `docs/api/auth-hardening.md`.
 */
function wrap<K extends string, T>(key: K, body: T): { [P in K]: T } {
  // The runtime object has exactly one key `K` typed as `T`; the cast is intentional.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return { [key]: body } as { [P in K]: T };
}

/**
 * Builds a `{ Authorization: 'Bearer ...' }` header, or undefined when the
 * token is absent/empty. Returning `undefined` is critical — it prevents the
 * HTTP call from emitting `Authorization: ` with an empty value.
 */
function bearerAuth(token: string | undefined): HttpHeaders | undefined {
  if (token === undefined || token.length === 0) {
    return undefined;
  }
  return new HttpHeaders({ Authorization: `Bearer ${token}` });
}

@Service()
export class AuthApiClient implements AuthStrategy {
  protected envService = inject(EnvironmentService);

  private readonly http = inject(HttpClient);

  get baseUrl(): string {
    return this.envService.getApiUrl();
  }

  /**
   * Sends a forgot-password request for the given email address.
   * @param email - The email address of the user requesting a password reset.
   * @returns An observable that completes when the request is sent.
   */
  forgotPassword(email: string) {
    return this.http.post<unknown>(
      `${this.baseUrl}/registration/forgot_password`,
      { email }
    );
  }

  /**
   * Retrieves the currently authenticated user. Authorization is provided by
   * the app-wide `authInterceptor`; this service does not set it explicitly.
   * @returns An observable that emits the connected {@link User}.
   */
  getConnectedUser() {
    return this.http.get<User>(`${this.baseUrl}/auth`);
  }

  /**
   * Authenticates a user with the given credentials.
   * @param email - The user's email address.
   * @param password - The user's password.
   * @returns An observable that emits a {@link LoginResponse} on success.
   */
  login(email: string, password: string) {
    return this.http.post<LoginResponse>(
      `${this.baseUrl}/session`,
      wrap('session', { email, password })
    );
  }

  /**
   * Registers a new user account.
   * @param user - The registration data for the new user.
   * @param accessToken - Optional invitation access token. When provided it is
   *   sent as a Bearer header; when absent, no Authorization header is sent at
   *   all (avoids leaking `Authorization: ` for anonymous signups).
   * @returns An observable that emits a {@link LoginResponse} on success.
   */
  register(user: RegisterUser, accessToken?: string) {
    const headers = bearerAuth(accessToken);
    return this.http.post<LoginResponse>(
      `${this.baseUrl}/registration`,
      wrap('registration', user),
      headers ? { headers } : {}
    );
  }

  /**
   * Resets the user's password using a reset token delivered via email.
   * @param password - The new password to set.
   * @param token - The password-reset token received via email.
   * @returns An observable that emits a {@link LoginResponse} on success.
   */
  resetPassword(password: string, token: string) {
    const headers = bearerAuth(token);
    return this.http.patch<LoginResponse>(
      `${this.baseUrl}/registration/reset_password`,
      { password },
      headers ? { headers } : {}
    );
  }
}
